import { ReactComponent as EditIcon } from "@/assets/dashboard/common/edit.svg";
import ControlledAutoComplete from "@/components/Form/InputFields/ControlledAutoComplete";
import PermissionGuard from "@/components/Guard/PermissionGuard";
import DeleteIcon from "@/components/Icon/DeleteIcon";
import Image from "@/components/Image/ImageSecondary";
import CustomImageLoader from "@/components/Loader/CustomImageLoader";
import CustomOverlapLoader from "@/components/Loader/CustomOverLapLoader";
import TableItemPlaceHolder from "@/components/Placeholder/TableItemPlaceHolder";
import CustomIconPopover from "@/components/Popover/CustomIconPopover";
import ActionCell from "@/components/Table/ActionCell";
import Cell from "@/components/Table/Cell";
import { StyledTableCell } from "@/components/Table/CustomTable";
import EditableCell, {
  useIsTableEditing,
} from "@/components/Table/editing";
import { MODULES } from "@/constants";
import { useDialogDispatch } from "@/context/DialogContext";
import { useSnackbarDispatch } from "@/context/SnackbarContext";
import useMediaFile from "@/hooks/useMediaFile";
import useScale from "@/hooks/useScale";
import routes from "@/routes";
import { Channel } from "@/types";
import {
  creativeAssetInfo,
  creativeSizeLabel,
  creativeTypeLabel,
} from "./listData";
import correctTypedErrorMessage from "@/utils/correctTypedErrorMessage";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IconButton, Tooltip } from "@mui/material";
import { Box } from "@mui/system";
import { QueryObserverResult } from "@tanstack/react-query";
import * as React from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import DisplayTemplate from "../../Template/DisplayTemplate";
import { convertColorFields } from "../../Template/helper";
import { useTemplateFieldsByTemplateMappingId } from "../../Template/template.hook";
import { TemplateVariant } from "../../Template/template.interface";
import {
  useDeleteAds,
  useGetBwCreativeDetails,
  useGetImageByUrn,
  useListAdCreativeApprovals,
} from "../asset.hook";
import {
  CreativeApiResponse,
  CreativeAssetType,
  CreativeByChannel,
  DisplayCreative,
  LinkedinCreative,
} from "../asset.interface";
import { isDisplayCreative } from "../asset.typeGuard";
import {
  getAssociatedLineItemsCount,
  getCreativeOptionsForTemplateView,
  getTemplateDataFromCreative,
} from "../CreateOrEditAsset/data";
import ImageAsset from "../ImageAsset";
import VendorApprovalCard from "../VendorApprovalCard";
import "./index.css";

const THUMBNAIL_WIDTH = 115;
const THUMBNAIL_HEIGHT = 36;
export default function AssetTableRow({
  row: creative,
  refetchCreatives,
  channel,
  removeCreative,
  headerLength,
}: AssetTableRowProps) {
  const { setDialogState } = useDialogDispatch();
  const { setSnackbarState } = useSnackbarDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryKeys = location.state?.queryKeys;
  const { data: creativeApprovals, isLoading: creativeApprovalLoading } =
    useListAdCreativeApprovals({
      queryParams: {
        advertiserId: creative?.advertiserId,
        creativeIds: [creative.id],
      },
    });
  const creativeApproved = creativeApprovals?.[0]?.approvals.some(
    (item: any) => item.approved
  );
  // Shared with the Size and Type columns' sort keys.
  const assetInfo: any = creativeAssetInfo(creative);

  const template =
    isDisplayCreative(creative) &&
    creative.CreativeTemplateDataValueMapping?.CreativeTemplateVariant;

  const handleClickOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setDialogState({
      open: true,
      title: creative.name,
      children: template ? (
        <TemplateViewModal
          creativeData={[creative] as unknown as DisplayCreative[]}
        />
      ) : (
        <CreativeViewModal creativeData={[creative]} />
      ),

      styles: {
        ".MuiDialog-paper": {
          maxWidth: "max-content",
          minWidth:
            creative?.channel === "display" && template
              ? { xs: "auto", md: "800px" }
              : "auto",
        },
      },
    });
  };

  const handleOnEdit = (
    e: React.MouseEvent<HTMLButtonElement>,
    isEditAllowed: boolean
  ) => {
    e.stopPropagation();
    if (!isEditAllowed) return;
    navigate(
      `${routes.DASHBOARD}/${
        routes.ASSETS.CREATIVES.INDEX
      }/${routes.ASSETS.CREATIVES.EDIT(creative.id)}?channel=${channel}`,
      {
        state: { creative: JSON.stringify(creative), queryKeys },
      }
    );
  };
  const { mutateAsync: deleteAd, isLoading: deletingCreative } = useDeleteAds();

  const handleOnDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    e.stopPropagation();

    setDialogState({
      open: true,
      title: "Confirmation",
      variant: "alert",
      buttonName: "ok",
      buttonLoading: false,
      buttonFunction: () => deleteFunction(id),
      children: (
        <h4 style={{ margin: 0 }}>Are you sure you want to delete creative</h4>
      ),
    });
  };

  const deleteFunction = async (id: number) => {
    try {
      setDialogState((state) => {
        return {
          ...state,
          buttonLoading: true,
        };
      });
      await deleteAd({
        id,
        type: channel,
      });
      await refetchCreatives();
      // Only the ListAsset page keeps creatives in local state; the Display and
      // LinkedIn lists are query-backed, so the refetch above is the whole job.
      removeCreative?.(id);
    } catch (error) {
      setSnackbarState({
        open: true,
        severity: "error",
        message: correctTypedErrorMessage(error),
      });
    } finally {
      setDialogState({
        open: false,
        title: "",
      });
    }
  };

  const editButtonState = getEditButtonState({
    channel: channel,
    isTemplateCreative: Boolean(template),
  });
  const isTableEditing = useIsTableEditing();
  if (creativeApprovalLoading)
    return (
      <StyledTableCell colSpan={headerLength}>
        <TableItemPlaceHolder />
      </StyledTableCell>
    );
  return (
    <>
      <Cell columnId="name">{creative.name}</Cell>
      {/* The one editable column. In view mode it renders exactly as a
          `<Cell />` would; the table's edit mode turns it into a field. */}
      <EditableCell columnId="clickUrl" row={creative} />
      <Cell columnId="lineItemAttached">
        {getAssociatedLineItemsCount(creative)}
      </Cell>
      <Cell columnId="size">{creativeSizeLabel(creative) ?? "_"}</Cell>
      <Cell columnId="vendorApproval">
        {creativeApprovals?.[0]?.approvals ? (
          <CustomIconPopover
            icon={creativeApproved ? CheckCircleIcon : CancelIcon}
            iconColor={creativeApproved ? "success" : "error"}
            triggerType="hover"
          >
            <VendorApprovalCard approvals={creativeApprovals?.[0]?.approvals} />
          </CustomIconPopover>
        ) : (
          <CancelIcon color="error" />
        )}
      </Cell>
      <Cell columnId="thumbnail" align="center">
        <Box
          sx={{
            width: `${THUMBNAIL_WIDTH}px`,
            height: `${THUMBNAIL_HEIGHT}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {channel === "display" && (
            <DisplayCreativeAsset<"thumbnail">
              key={creative.id}
              creative={creative as unknown as DisplayCreative}
              handleClickAsset={handleClickOpen}
              isThumbnail={true}
            />
          )}
          {channel === "linkedin" && (
            <LinkedinCreativeAsset<"thumbnail">
              key={creative.id}
              creative={creative as unknown as LinkedinCreative}
              handleClickAsset={handleClickOpen}
              isThumbnail={true}
            />
          )}
        </Box>
      </Cell>
      <Cell columnId="type">
        {creativeTypeLabel({ ...creative, assetInfo }, channel)}
      </Cell>
      <ActionCell>
        {/* Both buttons stand down while the table is being edited. Neither is
            reachable from CustomTable — the row is the caller's — so every list
            adopting edit mode has to do this itself. Leaving them live means
            Delete removes a row that still holds an unsaved draft, and Edit
            navigates away with the whole edit. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PermissionGuard module={MODULES.CREATIVES} permissionType="update">
            <Tooltip title={isTableEditing ? "" : editButtonState.tooltip}>
              <span>
                <IconButton
                  onClick={(e) => handleOnEdit(e, !editButtonState.disabled)}
                  disabled={isTableEditing}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          </PermissionGuard>
          <PermissionGuard module={MODULES.CREATIVES} permissionType="delete">
            <Tooltip title={isTableEditing ? "" : "Delete"}>
              <span>
                <IconButton
                  onClick={(e) => handleOnDelete(e, creative.id)}
                  disabled={deletingCreative || isTableEditing}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </PermissionGuard>
        </Box>
      </ActionCell>
    </>
  );
}

interface AssetTableRowProps {
  row: CreativeByChannel<Channel>;
  selected: Array<any>;
  handleClickCheckBox: (event: React.MouseEvent<unknown>, name: string) => void;
  refetchCreatives: () => Promise<
    QueryObserverResult<CreativeApiResponse, unknown>
  >;
  channel: Channel;
  removeCreative?: (creativeId: number) => void;
  headerLength: number;
}

const PreviewTemplate = (props: {
  template: TemplateVariant;
  templateValues: Record<string, any>;
  isOverlay?: boolean;
}) => {
  const { templateValues, template, isOverlay } = props;

  return (
    <DisplayTemplate
      isEdit={
        templateValues && Object.keys(templateValues)?.length > 0 ? true : false
      }
      isOverlay={false}
      values={templateValues}
      template={template.templateData}
      width={template.dimensions.width}
      height={template.dimensions.height}
    />
  );
};
export const PreviewTemplateThumbnail = (props: {
  template: TemplateVariant;
  templateValues: Record<string, any>;
  isOverlay?: boolean;
  thumbnailWidth: number;
  thumbnailHeight: number;
}) => {
  const {
    templateValues,
    template,
    isOverlay,
    thumbnailHeight,
    thumbnailWidth,
  } = props;
  const { ref: scaleRef } = useScale(thumbnailWidth, thumbnailHeight);

  return (
    <Box
      sx={{
        width: `${thumbnailWidth}px`,
        height: `${thumbnailHeight}px`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <Box
        ref={scaleRef}
        sx={{
          width: `calc(${template.dimensions.width}px + 2px)`,
          height: `calc(${template.dimensions.height}px + 2px)`,
        }}
      >
        <DisplayTemplate
          isEdit={
            templateValues && Object.keys(templateValues)?.length > 0
              ? true
              : false
          }
          isOverlay={isOverlay}
          values={templateValues}
          template={template.templateData}
          width={template.dimensions.width}
          height={template.dimensions.height}
        />
      </Box>
    </Box>
  );
};

export function TemplateViewModal({
  creativeData,
}: {
  creativeData: DisplayCreative[];
}) {
  const [selectedCreativeId, setSelectedCreativeId] = React.useState<string>(
    String(creativeData[0]?.id)
  );
  const selectedCreative = creativeData.find(
    (item) => String(item.id) === selectedCreativeId
  );

  const { template, templateDataValues, templateMappingId } =
    getTemplateDataFromCreative(selectedCreative);
  const [selectedTemplateValue, setSelectedTemplateValue] =
    React.useState<string>("BASE");

  const { data, isLoading } = useTemplateFieldsByTemplateMappingId({
    templateMappingId,
  });

  const allTemplateDataValues = data ?? templateDataValues ?? [];
  const base = allTemplateDataValues.find((item) => item.key === "BASE");
  const baseFields = base?.fields ? convertColorFields(base.fields) : null;
  if (!base || !baseFields) return <div>no data</div>;
  const fieldsExceptBase = allTemplateDataValues.filter(
    (item) => item.key !== "BASE"
  );
  const itemsObject = {
    [base.key as string]: {
      label: base.key,
      fields: baseFields,
    },
  };
  fieldsExceptBase.forEach((item) => {
    itemsObject[item.key as string] = {
      label: item.key as string,
      fields: item?.fields ? { ...baseFields, ...item.fields } : {},
    };
  });

  const itemList = Object.keys(itemsObject).map((key) => ({
    label: key,
    value: key,
  }));

  const handleResetSelectedTemplateValue = (creativeId: unknown) => {
    if (
      creativeId &&
      typeof creativeId === "string" &&
      creativeId !== selectedCreativeId &&
      selectedTemplateValue !== "BASE"
    ) {
      setSelectedTemplateValue("BASE");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        height: "100%",
        flex: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ width: "350px" }}>
          <ControlledAutoComplete
            label={"Variant"}
            value={selectedCreativeId}
            setValue={setSelectedCreativeId}
            itemList={getCreativeOptionsForTemplateView(creativeData)}
            customOnChange={handleResetSelectedTemplateValue}
          />
        </Box>
        <Box sx={{ width: "350px" }}>
          <ControlledAutoComplete
            label={"Ad Copy"}
            value={selectedTemplateValue}
            setValue={setSelectedTemplateValue}
            itemList={itemList}
            loading={isLoading}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pb: 2,
        }}
      >
        <CustomOverlapLoader isLoading={!template}>
          {template ? (
            <PreviewTemplate
              template={template}
              templateValues={itemsObject[selectedTemplateValue].fields}
              isOverlay={false}
            />
          ) : (
            <></>
          )}
        </CustomOverlapLoader>
      </Box>
    </Box>
  );
}

export function CreativeViewModal({
  creativeData,
}: {
  creativeData: CreativeByChannel<Channel>[];
}) {
  const selectedCreative = creativeData[0];

  if (!selectedCreative) return <div>no data</div>;

  const creativeChannel = selectedCreative.channel;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        height: "100%",
        maxHeight: "calc(100vh - 200px)",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflowY: "scroll",
        }}
      >
        <CustomOverlapLoader isLoading={!selectedCreative}>
          <>
            {selectedCreative && (
              <>
                {creativeChannel === "display" && (
                  <DisplayCreativeAsset
                    creative={selectedCreative as unknown as DisplayCreative}
                    handleClickAsset={undefined}
                    isThumbnail={false}
                  />
                )}
                {creativeChannel === "linkedin" && (
                  <LinkedinCreativeAsset
                    creative={selectedCreative as unknown as LinkedinCreative}
                    isThumbnail={false}
                    handleClickAsset={undefined}
                  />
                )}
              </>
            )}
          </>

          {/* <Box>
            {" "}
            {selectedCreative?.channel === "display" ? (
              <iframe
                src={imagesData?.[0]?.previewUrl}
                style={{
                  width: `calc(${assetInfo?.dimentions?.width}px + 10px)`,
                  height: `calc(${assetInfo?.dimentions?.height}px + 10px)`,
                }}
              ></iframe>
            ) : selectedCreative?.assetUrl ? (
              <iframe
                height={"500px"}
                width={"100%"}
                src={selectedCreative?.assetUrl}
              ></iframe>
            ) : (
              <Carousel
                showThumbs={false}
                swipeable={true}
                showIndicators={false}
                showStatus={false}
                className="carousel"
              >
                {imagesData.map((item, i) => (
                  <Image
                    key={i}
                    img={item.image}
                    alt={"image"}
                    loadedStatus={item.loadedStatus ?? "error"}
                  />
                ))}
              </Carousel>
            )}
          </Box> */}
        </CustomOverlapLoader>
      </Box>
    </Box>
  );
}

function getEditButtonState({
  channel,
  isTemplateCreative,
}: {
  channel: Channel;
  isTemplateCreative: boolean;
}) {
  if (channel !== "display" || isTemplateCreative) {
    return {
      disabled: true,
      tooltip: "You can't edit this creative",
    };
  }
  return {
    disabled: false,
    tooltip: "Edit",
  };
}

function DisplayCreativeAsset<
  Type extends "thumbnail" | "original-asset" = "original-asset"
>({
  creative,
  handleClickAsset,
  isThumbnail = false,
}: {
  creative: CreativeByChannel<"display">;
  handleClickAsset: Type extends "thumbnail"
    ? (e: React.MouseEvent<HTMLDivElement>) => void
    : undefined;
  isThumbnail?: boolean;
}) {
  const {
    adId,
    assetInfo = null,
    CreativeTemplateDataValueMapping = null,
  } = creative;

  const type: CreativeAssetType = CreativeTemplateDataValueMapping?.id
    ? "templateAd"
    : assetInfo?.mimeType === "video"
    ? "video"
    : "image";

  const {
    data: adDetails,
    isLoading,
    isFetching,
  } = useGetBwCreativeDetails({
    id: adId,
    options: {
      enabled: Boolean(type !== "templateAd" || !!adId),
      retry: 2,
      retryDelay: 5000,
    },
  });

  // Only parse a VAST XML wrapper (isPreviewLinkGenerated === false). A generated
  // preview link is a direct .mp4 used straight in <source>; fetching it here
  // would download the whole video just to (fail to) parse it as XML.
  const { mediaData, error } = useMediaFile(
    type === "video" && !adDetails?.isPreviewLinkGenerated
      ? adDetails?.previewUrl
      : null
  );

  const templateValues = convertColorFields(
    CreativeTemplateDataValueMapping?.CreativeTemplateDataValues?.[0]?.fields
  );
  const containerThumbnailStyles = isThumbnail
    ? {
        cursor: "pointer",
        "&:hover": {
          transform: "scale(1.1)",
        },
      }
    : {};

  const ImageComponent = isThumbnail ? ThumbnailImageAsset : ImageAsset;
  const VideoComponent = isThumbnail ? ThumbnailVideoAsset : VideoAsset;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        ...containerThumbnailStyles,
      }}
      onClick={handleClickAsset}
    >
      {isThumbnail &&
        type === "templateAd" &&
        CreativeTemplateDataValueMapping && (
          <PreviewTemplateThumbnail
            template={CreativeTemplateDataValueMapping.CreativeTemplateVariant}
            templateValues={templateValues}
            thumbnailWidth={THUMBNAIL_WIDTH}
            thumbnailHeight={THUMBNAIL_HEIGHT}
            isOverlay
          />
        )}
      {type === "image" && (
        <ImageComponent
          asset={{
            name: creative.name,
            image: isThumbnail
              ? adDetails?.thumbnailUrl
              : adDetails?.previewUrl,
            width: adDetails?.width ?? creative.assetInfo?.dimentions?.width,
            height: adDetails?.height ?? creative.assetInfo?.dimentions?.height,
          }}
          isLoadingAssetDetails={isLoading && isFetching}
        />
      )}
      {type === "video" &&
        (mediaData?.url || adDetails?.isPreviewLinkGenerated) && (
          <VideoComponent
            asset={{
              name: creative.name,
              image: adDetails?.thumbnailUrl,
              video: adDetails?.isPreviewLinkGenerated
                ? adDetails?.previewUrl
                : mediaData?.url ?? "",
            }}
            isLoadingAssetDetails={isLoading && isFetching}
          />
        )}
    </Box>
  );
}
function LinkedinCreativeAsset<
  Type extends "thumbnail" | "original-asset" = "original-asset"
>({
  creative,
  handleClickAsset,
  isThumbnail,
}: {
  creative: CreativeByChannel<"linkedin">;
  handleClickAsset: Type extends "thumbnail"
    ? (e: React.MouseEvent<HTMLDivElement>) => void
    : undefined;
  isThumbnail: Type extends "thumbnail" ? true : false;
}) {
  const { linkedinAssetUrn = "", assetInfo, advertiserId } = creative;
  const type: CreativeAssetType =
    assetInfo?.mimeType === "video" ? "video" : "image";

  const { data, isLoading } = useGetImageByUrn({
    id: linkedinAssetUrn,
    queryParams: {
      advertiserId,
    },
  });
  const ImageComponent = isThumbnail ? ThumbnailImageAsset : ImageAsset;
  const VideoComponent = isThumbnail ? ThumbnailVideoAsset : VideoAsset;

  const containerThumbnailStyles = isThumbnail
    ? {
        cursor: "pointer",
        "&:hover": {
          transform: "scale(1.1)",
        },
      }
    : {};
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        ...containerThumbnailStyles,
      }}
      onClick={handleClickAsset}
    >
      {type === "image" && (
        <ImageComponent
          asset={{
            name: creative.name,
            image: data?.downloadUrl,
            width: creative?.assetInfo?.dimentions?.width,
            height: creative?.assetInfo?.dimentions?.height,
          }}
          isLoadingAssetDetails={isLoading}
        />
      )}
      {type === "video" && (
        <VideoComponent
          asset={{
            name: creative.name,
            video: data?.downloadUrl,
            image: data?.thumbnail,
          }}
          isLoadingAssetDetails={isLoading}
        />
      )}
    </Box>
  );
}

function ThumbnailImageAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: {
    image: string;
    name: string;
    width: number | undefined;
    height: number | undefined;
  };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={THUMBNAIL_WIDTH}
      height={THUMBNAIL_HEIGHT}
      isLoading={isLoadingAssetDetails}
    >
      <Image
        img={asset.image}
        alt={asset.name}
        maxWidth={`${THUMBNAIL_WIDTH}px`}
        maxHeight={`${THUMBNAIL_HEIGHT}px`}
      />
    </CustomImageLoader>
  );
}

function VideoAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: { image: string; name: string; video: string };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={500}
      height={500}
      isLoading={isLoadingAssetDetails}
    >
      <video width={500} poster={asset.image} preload="metadata" controls>
        <source src={asset.video} type="video/mp4" />
      </video>
    </CustomImageLoader>
  );
}
function ThumbnailVideoAsset({
  asset,
  isLoadingAssetDetails,
}: {
  asset: { image: string; name: string; video: string };
  isLoadingAssetDetails: boolean;
}) {
  return (
    <CustomImageLoader
      width={THUMBNAIL_WIDTH}
      height={THUMBNAIL_HEIGHT}
      isLoading={isLoadingAssetDetails}
    >
      <video
        width={THUMBNAIL_WIDTH}
        height={THUMBNAIL_HEIGHT}
        poster={asset.image}
        preload="none"
      >
        <source src={asset.video} type="video/mp4" />
      </video>
    </CustomImageLoader>
  );
}
